/**
 * TanStack Query hooks for reported Issues (src/api/support-issues.ts).
 *
 * Every write invalidates the whole `supportIssueKeys.all` prefix — lists, the open Issue
 * and the assignee list — and the notification bell, since most writes notify someone.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { jsonFetch } from "@/lib/json-fetch";
import { notificationKeys, supportIssueKeys } from "@/lib/query";
import type { SupportIssuePriority, SupportIssueStatus } from "@/lib/support-issue-labels";
import type {
  SupportIssueAssignee,
  SupportIssueCreateResponse,
  SupportIssueDetail,
  SupportIssueListResponse,
} from "@/schemas/support-issues";

export interface SupportIssueListParams {
  scope: "mine" | "queue";
  status: "active" | "all" | SupportIssueStatus;
  owner?: "me" | "unassigned";
  search?: string;
  cursor?: string;
  direction?: "next" | "prev";
}

export function useSupportIssues(params: SupportIssueListParams, enabled = true) {
  return useQuery({
    queryKey: supportIssueKeys.list({ ...params }),
    queryFn: () => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) if (v) qs.set(k, String(v));
      return jsonFetch<SupportIssueListResponse>(`/api/support-issues?${qs.toString()}`);
    },
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useSupportIssue(id: string) {
  return useQuery({
    queryKey: supportIssueKeys.detail(id),
    queryFn: () => jsonFetch<SupportIssueDetail>(`/api/support-issues/${id}`),
  });
}

export function useSupportIssueAssignees(enabled: boolean) {
  return useQuery({
    queryKey: supportIssueKeys.assignees(),
    queryFn: () => jsonFetch<SupportIssueAssignee[]>("/api/support-issues/assignees"),
    enabled,
    staleTime: 5 * 60_000,
  });
}

function useInvalidateIssues() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: supportIssueKeys.all });
    qc.invalidateQueries({ queryKey: notificationKeys.all });
  };
}

function sendJson<T>(url: string, method: string, body?: unknown): Promise<T> {
  return jsonFetch<T>(url, {
    method,
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

/**
 * Submit a report. The form owns the toast: a failed submit keeps the form open with its
 * `clientSubmissionId`, so pressing Send again cannot make a second Issue.
 */
export function useCreateSupportIssue() {
  const invalidate = useInvalidateIssues();
  return useMutation({
    mutationFn: (form: FormData) =>
      jsonFetch<SupportIssueCreateResponse>("/api/support-issues", { method: "POST", body: form }),
    onSuccess: () => invalidate(),
  });
}

export interface UpdateSupportIssueInput {
  status?: SupportIssueStatus;
  priority?: SupportIssuePriority;
  ownerUserId?: string | null;
}

export function useUpdateSupportIssue(id: string) {
  const invalidate = useInvalidateIssues();
  return useMutation({
    mutationFn: (input: UpdateSupportIssueInput) =>
      sendJson<SupportIssueDetail>(`/api/support-issues/${id}`, "PATCH", input),
    onSuccess: () => {
      invalidate();
      toast.success("Issue updated");
    },
    onError: (err: Error) => toast.error(err.message || "Failed to update the issue"),
  });
}

export function useCommentOnSupportIssue(id: string) {
  const invalidate = useInvalidateIssues();
  return useMutation({
    mutationFn: (body: string) =>
      sendJson<SupportIssueDetail>(`/api/support-issues/${id}/comments`, "POST", { body }),
    onSuccess: () => {
      invalidate();
      toast.success("Comment added");
    },
    onError: (err: Error) => toast.error(err.message || "Failed to add the comment"),
  });
}

export function useCloseSupportIssue(id: string) {
  const invalidate = useInvalidateIssues();
  return useMutation({
    mutationFn: () => sendJson<SupportIssueDetail>(`/api/support-issues/${id}/close`, "POST"),
    onSuccess: () => {
      invalidate();
      toast.success("Issue closed");
    },
    onError: (err: Error) => toast.error(err.message || "Failed to close the issue"),
  });
}

export function useRetryDelyverPush(id: string) {
  const invalidate = useInvalidateIssues();
  return useMutation({
    mutationFn: () =>
      sendJson<SupportIssueDetail>(`/api/support-issues/${id}/delyver-retry`, "POST"),
    onSuccess: (issue) => {
      invalidate();
      if (issue.delyver?.status === "sent") toast.success("Sent to Delyver");
      else toast.error(issue.delyver?.lastError ?? "Delyver didn't accept it yet");
    },
    onError: (err: Error) => toast.error(err.message || "Failed to send to Delyver"),
  });
}
